'use client'

import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn } from '@qr-front/ui/utils'
import { showNotification } from 'components/Notification'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'

import { Image } from '@/generator/client'
import { qrCodesApi } from '@/generator/shared'
import { isAuthenticatedAtom } from '@/global-state'
import { getStore } from '@/store'

import {
	getOnboardingQrId,
	qrContentAtom,
	qrTypeAtom,
	qrUriAtom,
} from '../../features/generator/generatorState'
import {
	qrDesignAtom,
	qrDesignImageErrorAtom,
} from '../../features/generator/pages/QrDesignFormPage/qrDesignState'
import { getQrDataToEncode } from '../../features/generator/utils/buildQrLandingUrl'

export const CornerIcon = ({
	className,
	style,
}: {
	className?: string
	style?: CSSProperties
}) => {
	return (
		<svg
			width="29"
			height="29"
			viewBox="0 0 29 29"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={cn('absolute text-[#0A0909]', className)}
			style={style}
		>
			<path
				d="M2 26.0428V2"
				strokeWidth="4"
				strokeLinecap="round"
				stroke="currentColor"
			/>
			<path
				d="M2 2H26.3153"
				strokeWidth="4"
				strokeLinecap="round"
				stroke="currentColor"
			/>
		</svg>
	)
}

const RETRY_DELAY_MS = 1_500

export const OnboardingQrPreview = ({
	qrId,
	size = 120,
	frameLessSize = undefined,
	frameMargin = 18,
	className,
}: {
	qrId?: string | null
	size?: number
	frameLessSize?: number
	frameMargin?: number
	className?: string
}) => {
	const [qrUri, setQrUri] = useAtom(qrUriAtom)
	const [qrDesign, setQrDesign] = useAtom(qrDesignAtom)
	const qrContent = useAtomValue(qrContentAtom)
	const [qrDesignImageError, setQrDesignImageError] = useAtom(
		qrDesignImageErrorAtom,
	)
	const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
		'loading',
	)
	const t = useTranslations()
	const qrSize =
		frameLessSize && qrDesign?.frame.type === 'default'
			? frameLessSize
			: size

	useEffect(() => {
		let isCancelled = false

		const loadQr = async (id: string) => {
			const getQr = getStore().get(isAuthenticatedAtom)
				? qrCodesApi.getQr
				: qrCodesApi.getGuestQr
			const {
				data: {
					uri,
					content: { type },
					qrDesign,
				},
			} = await getQr(id)
			if (isCancelled) return
			setQrUri(uri)
			getStore().set(qrTypeAtom, type)
			setQrDesign(qrDesign)
		}

		// The prop carries the server-resolved QR of the authenticated user and
		// wins over the localStorage id, which can still point at an abandoned
		// guest QR from an earlier session (MYQRCODE-10746).
		const getCandidateIds = () =>
			[qrId, getOnboardingQrId()].filter(
				(id, index, ids): id is string =>
					!!id && ids.indexOf(id) === index,
			)

		const loadFirstAvailableQr = async () => {
			for (const id of getCandidateIds()) {
				try {
					await loadQr(id)
					return true
				} catch (error) {
					console.error(error)
				}
			}
			return false
		}

		const loadOnboardingQr = async () => {
			setStatus('loading')

			if (await loadFirstAvailableQr()) {
				if (!isCancelled) setStatus('loaded')
				return
			}

			await new Promise((resolve) =>
				window.setTimeout(resolve, RETRY_DELAY_MS),
			)
			if (isCancelled) return

			const isLoaded = await loadFirstAvailableQr()
			if (!isCancelled) setStatus(isLoaded ? 'loaded' : 'error')
		}

		void loadOnboardingQr()

		return () => {
			isCancelled = true
		}
	}, [qrId, setQrDesign, setQrUri])

	useEffect(() => {
		if (qrDesignImageError) {
			void showNotification(
				t(
					'generator__design_form__logo_section__invalid_image_message',
				),
				{ type: 'error', context: 'generator' },
			)
			setQrDesignImageError(null)
		}
	}, [qrDesignImageError, setQrDesignImageError, t])

	return (
		<div
			className={cn(
				'relative flex aspect-square w-full max-w-[254px] items-center justify-center rounded-10 bg-white shadow-generator desktop:max-w-[260px]',
				className,
			)}
		>
			{status === 'loading' && <Spinner size={24} color="blue" />}
			{status === 'error' && (
				<p className="type-body-small max-w-[70%] text-center text-grey">
					{t('public__generic__unexpected_error')}
				</p>
			)}
			{status === 'loaded' && qrUri && qrDesign && qrContent ? (
				<Image
					data={getQrDataToEncode({ qrUri, qrContent })}
					qrDesign={qrDesign}
					size={qrSize}
				/>
			) : null}
			<CornerIcon style={{ left: frameMargin, top: frameMargin }} />
			<CornerIcon
				className="rotate-90"
				style={{ right: frameMargin, top: frameMargin }}
			/>
			<CornerIcon
				className="-rotate-90"
				style={{ left: frameMargin, bottom: frameMargin }}
			/>
			<CornerIcon
				className="rotate-180"
				style={{ right: frameMargin, bottom: frameMargin }}
			/>
		</div>
	)
}
